'use client'

import { useEffect, useRef } from 'react'
import { getIntent, hasIntent, subscribeIntent } from '@/components/utils/registrationIntent'
import {
    IDLE_DELAY_MS,
    PROMO_READY_EVENT,
    PROMO_SUPPRESSED_EVENT,
    isTargetPath,
    isTargetViewport,
} from './config'

// 40秒の待機をリセットする、フォーム内の操作。
// iOS Safariはボタンのタップでfocusinが飛ばないためpointerdownも見る
const FORM_RESET_EVENT_TYPES = ['focusin', 'pointerdown', 'input'] as const
const FORM_SELECTOR = '#entry-form'

// 同一ページでの二重発火防止。GTM側のトリガーも「1ページに1回」にしてあるが、こちらでも止める
let hasFired = false

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] }

const pushDataLayer = (payload: Record<string, unknown>): void => {
    const w = window as DataLayerWindow
    w.dataLayer = w.dataLayer || []
    w.dataLayer.push(payload)
}

const isFormInteraction = (event: Event): boolean =>
    event.target instanceof Element && !!event.target.closest(FORM_SELECTOR)

interface UseGenieeLinePromoParams {
    currentStep: number
    jobChangeInterest: string
}

export const useGenieeLinePromo = ({
    currentStep,
    jobChangeInterest,
}: UseGenieeLinePromoParams): void => {
    // 発火時点の値を計測パラメータとして送るためだけに参照する。
    // これらをuseEffectの依存に入れるとstepが進むたびに待機が張り直されるため、refで渡す
    const snapshot = useRef({ currentStep, jobChangeInterest })
    snapshot.current = { currentStep, jobChangeInterest }

    // 待機の起点はページ流入時（このコンポーネントのマウント時）。
    // 転職意欲ポップ（ステップ0）が開いている間もカウントする。
    useEffect(() => {
        if (!isTargetPath(window.location.pathname)) return
        if (!isTargetViewport()) return
        if (hasFired) return

        let timerId: number | undefined
        // バックグラウンドタブで開かれた場合、最初にフォアグラウンドになった時点は
        // 「再フォーカス」ではなく初回表示なので、待機を始めるだけにする（滞在0秒での発火を防ぐ）
        let hasBeenVisible = document.visibilityState !== 'hidden'

        const clearTimer = (): void => {
            if (timerId !== undefined) {
                window.clearTimeout(timerId)
                timerId = undefined
            }
        }

        // 戻るボタンだけは離脱の合図として扱うため、ステップに関わらず常時表示する。
        // 40秒待機と再フォーカスは、ステップ2以降に進んだ人には表示しない
        const fire = (trigger: 'idle' | 'refocus' | 'back'): void => {
            if (hasFired) return
            if (trigger !== 'back' && hasIntent()) return
            hasFired = true
            clearTimer()

            pushDataLayer({
                event: PROMO_READY_EVENT,
                promo_trigger: trigger,
                promo_idle_ms: IDLE_DELAY_MS,
                promo_step_at_fire: snapshot.current.currentStep,
                promo_job_change_interest: snapshot.current.jobChangeInterest,
            })
        }

        const arm = (): void => {
            if (hasFired || hasIntent()) return
            clearTimer()
            if (document.visibilityState === 'hidden') return
            timerId = window.setTimeout(() => fire('idle'), IDLE_DELAY_MS)
        }

        // 裏に回っている間は待機を止め、フォアグラウンドに戻った時点で表示する。
        // ステップ2以降は fire() 側で抑止されるため出ない
        const handleVisibilityChange = (): void => {
            if (document.visibilityState === 'hidden') {
                clearTimer()
                return
            }
            if (!hasBeenVisible) {
                hasBeenVisible = true
                arm()
                return
            }
            fire('refocus')
        }

        const handleFormEvent = (event: Event): void => {
            if (isFormInteraction(event)) arm()
        }

        const handleScroll = (): void => arm()

        // 戻るボタンの検知。ダミーの履歴を1つ積んでおき、戻る操作をpopstateで捕まえる。
        // 表示前なら、戻る操作はPOPの表示に使われてページに留まる（離脱POPの定番手法）。
        // 表示済みの場合はダミーを消費するだけになり「戻るが無反応」になってしまうため、
        // 積んだ1つぶんだけ素通しさせる
        window.history.pushState({ genieeLinePromo: true }, '')
        let hasPassedThroughBack = false
        const handlePopState = (): void => {
            if (!hasFired) {
                fire('back')
                return
            }
            if (hasPassedThroughBack) return
            hasPassedThroughBack = true
            window.history.back()
        }

        const unsubscribeIntent = subscribeIntent((reason) => {
            // 発火済みなら「抑止した」ではないので計測もしない
            if (hasFired) return
            clearTimer()
            pushDataLayer({
                event: PROMO_SUPPRESSED_EVENT,
                promo_suppress_reason: reason,
                promo_step_at_suppress: snapshot.current.currentStep,
                promo_suppress_restored: false,
            })
        })

        // リロードや他LPからの遷移で、このページに来た時点で既に抑止されているケース。
        // subscribeIntent は markIntent が実際に呼ばれたときしか発火せず、markIntent は
        // 記録済みなら早期returnするため、ここで計上しないと「抑止された人」の分母が欠ける
        const restoredReason = getIntent()
        if (restoredReason) {
            pushDataLayer({
                event: PROMO_SUPPRESSED_EVENT,
                promo_suppress_reason: restoredReason,
                promo_step_at_suppress: snapshot.current.currentStep,
                promo_suppress_restored: true,
            })
        }

        FORM_RESET_EVENT_TYPES.forEach((type) =>
            document.addEventListener(type, handleFormEvent)
        )
        window.addEventListener('scroll', handleScroll, { passive: true })
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('popstate', handlePopState)
        arm()

        return () => {
            clearTimer()
            unsubscribeIntent()
            FORM_RESET_EVENT_TYPES.forEach((type) =>
                document.removeEventListener(type, handleFormEvent)
            )
            window.removeEventListener('scroll', handleScroll)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('popstate', handlePopState)
        }
        // currentStep/jobChangeInterestはsnapshot経由で参照する（依存に入れると待機が張り直される）
    }, [])
}
